import { z } from 'zod';
import { DimensionValueGroupingType, type DimensionValueGroupingToken } from '@sleed/bizcommerce-api';

/**
 * Dimension value grouping edit form. Mirrors `DimensionValueGroupingToken` and the legacy admin grid
 * (Core/client/admin/src/pages/Catalog/DimensionsValueGrouping/index.js): nm required ≤100, cd required ≤100,
 * dgt (grouping type) lookup, chex (group swatch colour, wins over the values' own, BW-8411), iv boolean, si required. The grouping type is a lookup over
 * DimenstionValueGroupingApi.getDimenstionValueGroupingTypes (EnumToken id→name). did (BW-8412): the Custom dimension a
 * grouping without a type belongs to; its values must be values of that dimension. erpcd (BW-8410): optional ERP sync code ≤50.
 */
export const dimensionGroupingSchema = z.object({
	id: z.number().optional(),
	nm: z.string().trim().min(1, 'validation.required').max(100, 'validation.tooLong'),
	cd: z.string().trim().min(1, 'validation.required').max(100, 'validation.tooLong'),
	erpcd: z.string().trim().max(50, 'validation.tooLong').nullable(),
	dgt: z.number({ message: 'validation.required' }),
	chex: z.string().trim().max(10, 'validation.tooLong').nullable(),
	did: z.number().nullable(),
	iv: z.boolean(),
	si: z.number({ message: 'validation.required' }).int()
});

export type DimensionGroupingFormValues = z.infer<typeof dimensionGroupingSchema>;

export const EMPTY_DIMENSION_GROUPING: DimensionGroupingFormValues = {
	nm: '',
	cd: '',
	erpcd: '',
	dgt: 0,
	chex: null,
	did: null,
	iv: true,
	si: 0
};

export function tokenToForm(token: DimensionValueGroupingToken): DimensionGroupingFormValues {
	return {
		id: token.id,
		nm: token.nm ?? '',
		cd: token.cd ?? '',
		erpcd: token.erpcd ?? '',
		dgt: (token.dgt as number | undefined) ?? 0,
		chex: token.chex ?? null,
		did: token.did ?? null,
		iv: token.iv ?? false,
		si: token.si ?? 0
	};
}

export function formToToken(form: DimensionGroupingFormValues): DimensionValueGroupingToken {
	return {
		id: form.id,
		nm: form.nm.trim(),
		cd: form.cd.trim(),
		erpcd: form.erpcd?.trim() || null,
		dgt: form.dgt as DimensionValueGroupingType,
		chex: form.chex?.trim() || null,
		did: form.dgt === DimensionValueGroupingType.None ? form.did : null,
		iv: form.iv,
		si: form.si
	};
}
